import { faPencil, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { formatDate } from '@squaredup/utilities';
import { ColumnDef } from '@tanstack/react-table';
import { orderBy } from 'lodash';
import { ApplicationTable } from 'pages/components/ApplicationTable/ApplicationTable';
import { Action } from 'pages/components/ApplicationTable/types';
import PluginIcon from 'pages/scope/PluginIcon';
import { FC } from 'react';
import TruncateWithTooltip from '../TruncateWithTooltip';
import { CustomDataStreamProjectedRow } from './DataStreamDefinitions';

interface DataStreamDefinitionsTableProps {
    dataStreamDefinitions: CustomDataStreamProjectedRow[];
    onEdit: (customType: CustomDataStreamProjectedRow) => void;
    onDelete: (workspace: CustomDataStreamProjectedRow) => void;
}

const DataStreamApplicationTable = ApplicationTable<CustomDataStreamProjectedRow, string>();

const DataStreamDefinitionsTable: FC<DataStreamDefinitionsTableProps> = ({
    dataStreamDefinitions,
    onEdit,
    onDelete
}) => {
    const columns: ColumnDef<CustomDataStreamProjectedRow>[] = [
        {
            id: 'name',
            header: 'Name',
            cell: ({ row }) => {
                return (
                    <TruncateWithTooltip title={row.original.displayName || ''}>
                        <div className='truncate'>{row.original.displayName}</div>
                    </TruncateWithTooltip>
                );
            },
            size: 300,
            accessorKey: 'displayName'
        },
        {
            id: 'pluginName',
            header: 'Data Source',
            cell: ({ row }) => {
                return (
                    <TruncateWithTooltip title={row.original.pluginName || ''}>
                        <PluginIcon className='h-6 mr-2' pluginName={row.original.pluginName || ''} />
                        <div className='truncate'>{row.original.pluginName}</div>
                    </TruncateWithTooltip>
                );
            },
            size: 300,
            accessorKey: 'pluginName'
        },
        {
            id: 'id',
            header: 'Id',
            cell: ({ row }) => {
                return (
                    <TruncateWithTooltip title={row.original.id || ''}>
                        <div className='truncate'>{row.original.id}</div>
                    </TruncateWithTooltip>
                );
            },
            size: 300,
            accessorKey: 'id'
        },
        {
            id: 'lastUpdated',
            header: 'Last Updated',
            cell: ({ row }) => {
                if (row.original.lastUpdated == null) {
                    return 'unknown';
                }

                return formatDate(row.original.lastUpdated);
            },
            sortingFn: (a, b) => { 
                if (a.original.lastUpdated == null || b.original.lastUpdated == null) { 
                    // If either date is null, we can't compare them, so we return 0 to keep the original order
                    return 0;
                }

                return new Date(a.original.lastUpdated).valueOf() - new Date(b.original.lastUpdated).valueOf();
            },
            size: 300,
            accessorKey: 'id'
        }
    ];

    const actions: Action[] = [
        {
            action: onEdit,
            icon: faPencil,
            dataTestId: 'edit',
            tooltip: 'Edit custom data stream'
        },
        {
            action: onDelete,
            icon: faTrash,
            dataTestId: 'delete',
            tooltip: 'Delete custom data stream'
        }
    ];

    return (
        <div className='flex flex-col flex-1 w-full h-full min-h-0'>
            <DataStreamApplicationTable
                config={{
                    actions,
                    noDataMessage: 'There are no custom data streams configured.'
                }}
                columns={columns}
                data={orderBy(dataStreamDefinitions, ['pluginName', 'displayName'], ['asc', 'asc'])}
            />
        </div>
    );
};

export default DataStreamDefinitionsTable;
